import { ArrayHelpers, FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ArtikelNutztierfutterFormular, mapArtikelToOptions } from '../../helper/artikel-helper';
import { useDispatch, useSelector } from 'react-redux';
import { Option } from '../../helper/select-options';
import { getWerkeForArtikel } from '../../../store/Abholwerk.store';
import { v4 as uuidv4 } from 'uuid';
import styles from './Artikelauswahl.module.scss';
import textItemStyles from '../../ui-components/TextItem/TextItem.module.scss';
import SelectInput from '../../ui-components/SelectInput/SelectInput';
import Button from '../../ui-components/Button/Button';
import { ReactElement, useEffect, useState } from 'react';
import { MenuList } from './MenuList/MenuList';
import { ArtikelSackwarenSelectors } from '../../../store/ArtikelSackware.store';
import { selectArtikelLoseWareIsLoading, selectArtikelLoseWareList } from '../../../store/ArtikelLoseWare.store';
import { Berechnungsart } from '../../types/enums';
import { emptyArtikelLoseWare } from '../../types/defaultValues';
import type { ArtikelLoseWare, ArtikelSackware, BestellpositionArtikelLoseWareView } from '../../types';
import { NEWID_PREFIX } from '../../constants';
import { useRechnungsempfaengerFromBestellung } from '../../hooks/useRechnungsempfaengerFromBestellung';
import { useWarenempfaengerFromBestellung } from '../../hooks/useWarenempfaengerFromBestellung';
import { createArtikelZulageFilter } from '../../hooks/useArtikelZulagePartnerContext';
import { selectListOfSelectedArtikel, setArtikelNummern } from '../../../store/Artikelauswahl.store';
import { getArtikelZulagen } from '../../../store/ArtikelZulage.store';
import { components, OptionProps, OptionTypeBase } from 'react-select';
import iconHeartFilled from '../../../assets/icon-heart-filled.svg';
import iconHeartOutline from '../../../assets/icon-heart-outline.svg';
import { putData } from '../../fetchApi';

interface ArtikelauswahlNutztierfutterProps {
    artikelSackwareList?: ArtikelSackware[];
    artikelLoseWareList?: ArtikelLoseWare[];
    allowAddingNewArticles?: boolean;
}

export const Artikelauswahl = ({
    artikelSackwareList,
    artikelLoseWareList,
    allowAddingNewArticles = true,
}: ArtikelauswahlNutztierfutterProps): ReactElement => {
    const selectedArtikelNummern = useSelector(selectListOfSelectedArtikel);
    const loseWareIsLoading = useSelector(selectArtikelLoseWareIsLoading);
    const sackwareIsLoading = useSelector(ArtikelSackwarenSelectors.loading);
    const artikelLoading = sackwareIsLoading || loseWareIsLoading;
    const [selectOptions, setSelectOptions] = useState<Option[]>([]);
    const [favoriteArtikelNummern, setFavoriteArtikelNummern] = useState<string[]>([]);
    const [favoritePending, setFavoritePending] = useState<Set<string>>(new Set());

    useEffect(() => {
        const artikelSackwareOptions = artikelSackwareList?.map(mapArtikelToOptions) ?? [];
        const artikelLoseWareOptions = artikelLoseWareList?.map(mapArtikelToOptions) ?? [];
        const mergedMap = new Map<string, Option>();
        artikelSackwareOptions.concat(artikelLoseWareOptions).forEach((opt) => {
            if (!mergedMap.has(opt.value)) {
                mergedMap.set(opt.value, opt);
            }
        });
        const merged = Array.from(mergedMap.values());

        const sorted = merged.sort((a, b) => {
            const favA = Boolean(a.favorite);
            const favB = Boolean(b.favorite);
            if (favA !== favB) return favA ? -1 : 1;
            return String(a.label).localeCompare(String(b.label), 'de', { sensitivity: 'base' });
        });

        setSelectOptions(sorted);

        const favoritesFromApi = sorted.filter((o) => Boolean(o.favorite)).map((o) => String(o.value));
        setFavoriteArtikelNummern(favoritesFromApi);
    }, [artikelLoseWareList, artikelSackwareList, selectedArtikelNummern]);

    async function onToggleFavorite(artikelNummer: string): Promise<void> {
        if (favoritePending.has(artikelNummer)) return;

        const wasFavorite = favoriteArtikelNummern.includes(artikelNummer);
        const nextFavorite = !wasFavorite;

        setFavoritePending((prev) => new Set(prev).add(artikelNummer));

        const applyLocal = (isFavorite: boolean): void => {
            setFavoriteArtikelNummern((prev) =>
                isFavorite ? Array.from(new Set([...prev, artikelNummer])) : prev.filter((n) => n !== artikelNummer)
            );
            setSelectOptions((prev) => {
                const next = prev.map((o) => (String(o.value) === artikelNummer ? { ...o, favorite: isFavorite } : o));
                return next.sort((a, b) => {
                    const favA = Boolean(a.favorite);
                    const favB = Boolean(b.favorite);
                    if (favA !== favB) return favA ? -1 : 1;
                    return String(a.label).localeCompare(String(b.label), 'de', { sensitivity: 'base' });
                });
            });
        };

        applyLocal(nextFavorite);

        try {
            await putData<void>(`artikel/favoriten/${artikelNummer}`, { favorite: nextFavorite });
        } catch (e) {
            // Falls der Request scheitert (z. B. Auth/Netzwerk), setzen wir den UI-Status zurück.
            // eslint-disable-next-line no-console
            console.error('Konnte Artikel-Favorit nicht speichern', { artikelNummer, nextFavorite, error: e });
            applyLocal(wasFavorite);
        } finally {
            setFavoritePending((prev) => {
                const next = new Set(prev);
                next.delete(artikelNummer);
                return next;
            });
        }
    }

    const ArtikelOption = (props: OptionProps<OptionTypeBase, false>): ReactElement => {
        const artikelNummer = String(props.data.value ?? '');
        const isFavorite = Boolean((props.data as Option).favorite) || favoriteArtikelNummern.includes(artikelNummer);
        const isPending = favoritePending.has(artikelNummer);

        return (
            <components.Option {...props}>
                <div className={styles._artikelOptionRow}>
                    <span className={styles._artikelOptionLabel}>{props.label}</span>
                    <button
                        type="button"
                        className={styles._favoriteButton}
                        disabled={isPending}
                        onMouseDown={(event): void => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onClick={(event): void => {
                            event.preventDefault();
                            event.stopPropagation();
                            void onToggleFavorite(artikelNummer);
                        }}
                        aria-label={isFavorite ? 'Artikel als Favorit entfernen' : 'Artikel als Favorit markieren'}
                    >
                        <img
                            src={isFavorite ? iconHeartFilled : iconHeartOutline}
                            alt=""
                            aria-hidden="true"
                            className={styles._heartIcon}
                        />
                    </button>
                </div>
            </components.Option>
        );
    };

    return (
        <FieldArray
            name="bestellpositionenView"
            render={(arrayHelpers): ReactElement => (
                <>
                    <div className={styles._searchContainer}>
                        <div className={styles._selectContainer}>
                            {artikelLoading ? (
                                <div className={textItemStyles._text}>Artikel werden geladen</div>
                            ) : (
                                <SelectInput
                                    classNamePrefix={'react_select__form-artikel'}
                                    id="addArticle"
                                    name="addArticle"
                                    options={selectOptions}
                                    components={{ MenuList, Option: ArtikelOption }}
                                    placeholder="Artikel hier eingeben (Suchfunktion)"
                                    hideNativeSelect={true}
                                    isClearable={false}
                                    autoSelectIfOnlyOneOption
                                />
                            )}
                        </div>
                        <AddArtikelButton arrayHelpers={arrayHelpers} />
                    </div>
                    {allowAddingNewArticles ? <AddNewArtikel arrayHelpers={arrayHelpers} /> : null}
                </>
            )}
        />
    );
};

type AddArtikelButtonProps = {
    arrayHelpers: FieldArrayRenderProps;
};

const AddArtikelButton = ({ arrayHelpers }: AddArtikelButtonProps): ReactElement => {
    const formikProps = useFormikContext<ArtikelNutztierfutterFormular>();
    const dispatch = useDispatch();
    const artikelSackwareList = useSelector(ArtikelSackwarenSelectors.list);
    const rechnungsempfaengerFromBestellung = useRechnungsempfaengerFromBestellung();
    const warenempfaengerFromBestellung = useWarenempfaengerFromBestellung();
    const artikelLoseWareList = useSelector(selectArtikelLoseWareList);
    const selectedArtikelNummern = useSelector(selectListOfSelectedArtikel);

    function onAddArtikel(): void {
        const artikelToAdd = formikProps.values.addArticle;
        // const alreadyInBestellpositionen = selectedArtikelNummern.find((artikel) => artikel === artikelToAdd);

        // if (alreadyInBestellpositionen) {
        //     return;
        // }

        const artikelSackware = artikelSackwareList.find((a) => a.nummer === artikelToAdd);
        if (artikelSackware) {
            arrayHelpers.push({
                ...artikelSackware,
                mengeVe: '',
                mengePal: '',
                werkId: '',
            });
        }
        const artikelLoseWare = artikelLoseWareList.find((a) => a.nummer === artikelToAdd);
        if (artikelLoseWare) {
            const neueBestellposition: BestellpositionArtikelLoseWareView = {
                ...artikelLoseWare,
                menge: '',
                werkId: '',
                zulage: false,
                berechnungsart: Berechnungsart.KeinVorgabe,
            };
            arrayHelpers.push(neueBestellposition);
        }
        if (artikelToAdd) {
            dispatch(setArtikelNummern([...selectedArtikelNummern, artikelToAdd]));
        }
        if (artikelToAdd && rechnungsempfaengerFromBestellung?.partnerNummer) {
            dispatch(getWerkeForArtikel(artikelToAdd, rechnungsempfaengerFromBestellung.partnerNummer));

            // Lade auch Zulagen für den ausgewählten Artikel
            const selectedArtikel = artikelSackware || artikelLoseWare;
            if (selectedArtikel?.artikelGruppeId && warenempfaengerFromBestellung?.partnerNummer) {
                const filter = createArtikelZulageFilter(String(selectedArtikel.artikelGruppeId), undefined, {
                    warenempfaengerPartnerNummer: warenempfaengerFromBestellung.partnerNummer,
                    rechnungsempfaengerPartnerNummer: rechnungsempfaengerFromBestellung?.partnerNummer ?? '',
                });
                if (filter) {
                    dispatch(getArtikelZulagen(filter));
                }
            }
        }
    }

    return (
        <div className={styles._artikelButtonContainer}>
            <Button data-cy="workflow-artikelauswaehlen" type="button" onClick={onAddArtikel}>
                <>
                    <span className={styles._hinzufuegenText}>Artikel auswählen</span>
                    <span className={styles._hinzufuegenPlus}>+</span>
                </>
            </Button>
        </div>
    );
};

type AddNewArtikelProps = {
    arrayHelpers: FieldArrayRenderProps;
};

const AddNewArtikel = ({ arrayHelpers }: AddNewArtikelProps): ReactElement => {
    const dispatch = useDispatch();
    const rechnungsempfaengerFromBestellung = useRechnungsempfaengerFromBestellung();

    function onAddNewArtikel(arrayHelpers: ArrayHelpers): void {
        const artikelNummer = NEWID_PREFIX + uuidv4();
        if (artikelNummer && rechnungsempfaengerFromBestellung?.partnerNummer) {
            dispatch(getWerkeForArtikel(artikelNummer, rechnungsempfaengerFromBestellung?.partnerNummer));
            arrayHelpers.push({
                ...emptyArtikelLoseWare,
                nummer: artikelNummer,
                mengeVe: '',
                mengePal: '',
                werkId: '',
                berechnungsart: Berechnungsart.KeinVorgabe,
            });
        }
    }

    return (
        <div className={styles._neuerArtikel}>
            <span className={styles._neuerArtikelText}>Sie finden einen Artikel nicht?</span>
            <div className={styles._artikelButtonContainer}>
                <Button data-cy="lw-erfassen" isSecondary={true} type="button" onClick={(): void => onAddNewArtikel(arrayHelpers)}>
                    <>
                        <span className={styles._hinzufuegenText}>Fehlenden Artikel erfassen</span>
                        <span className={styles._hinzufuegenPlus}>+</span>
                    </>
                </Button>
            </div>
        </div>
    );
};
